import { Injectable, inject } from '@angular/core';

import {
  API_BASE,
} from '../../constants/general.constants';

import { EMPTY, Observable, Subject, of, shareReplay, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import {
  NewWorkflow,
  Workflow,
  WorkflowCreate,
  WorkflowUpdate,
} from '../../interfaces/project.interface';
import { FullWorkflow } from '@app/shared/interfaces';

import { GeneralHelpers } from '../../helpers/general.helper';
import { LocalstorageHelper } from '../../helpers/localstorage.helper';
import { AppStateService } from '../app-state/app-state.service';
import { AppEventBus } from '../events/app-events';

/** What deleting a workflow would remove (EHQ-512). */
export interface WorkflowDeletionImpact {
  jobCount: number;
  executingCount: number;
}

@Injectable({
  providedIn: 'root',
})
export class WorkflowService {
  private readonly http = inject(HttpClient);

  readonly #appState = inject(AppStateService);
  private readonly appEventBus = inject(AppEventBus);

  constructor() {
    // SCRUM-751: a workflow_variable_schema change on the watched project
    // invalidates cached workflow data for that workflow so the next read
    // reflects the new schema. The schema itself is only surfaced on demand
    // (the edit dialog and the operator run-now prompt both fetch fresh on
    // open), so there is no persistent view to refresh in place — invalidating
    // the cache is the correct, low-risk action. Singleton service, so this
    // lives for the app lifetime.
    this.appEventBus.on('reloadWorkflowVariableSchema').subscribe((event) => {
      this.invalidateWorkflowCaches(event.workflowId);
    });
  }

  public $setWorkflowIdSubject = new Subject<string>();

  public workflowList: Workflow[] = [];

  // ✅ Cache for workflows to prevent duplicate API calls
  private workflowsCache: Map<string, { data: any; timestamp: number; request$: Observable<any> | null }> = new Map();
  private fullWorkflowCache: Map<string, { data: any; timestamp: number; request$: Observable<any> | null }> = new Map();
  private readonly CACHE_DURATION = 10000;

  /**
   * Drop cached workflow data for a single workflow so the next read refetches
   * (SCRUM-751). Full-workflow cache keys are `${projectId}_${versionId}_${workflowId}`;
   * the workflow-list cache embeds each workflow (including variableSchema), so
   * it is cleared wholesale — it rebuilds on the next read within the 10s TTL.
   */
  public invalidateWorkflowCaches(workflowId: number): void {
    const suffix = `_${workflowId}`;
    for (const key of Array.from(this.fullWorkflowCache.keys())) {
      if (key.endsWith(suffix)) {
        this.fullWorkflowCache.delete(key);
      }
    }
    this.workflowsCache.clear();
  }

  // ────────────────────────────────────────────────────────────────────────────────
  // Create Workflow

  public create(
    data: WorkflowCreate,
    projectId: string | null
  ): Observable<NewWorkflow | never> {
    if (projectId === null) {
      return EMPTY;
    }
    return this.http.post<NewWorkflow>(
      `${API_BASE}/project/${projectId}/v/${this.#appState.versionId()}/workflow`,
      data
    );
  }

  // ─────────────────────────────────────────────────────────────────────
  // Get by id

  public getById(workflowId: string, projectId: string): Observable<Workflow> {
    return this.http.get<Workflow>(
      `${API_BASE}/project/${projectId}/v/${this.#appState.versionId()}/workflow/${workflowId}`
    );
  }

  // ─────────────────────────────────────────────────────────────────────
  // Update

  /** A workflow's name can't be changed (EHQ-409), so WorkflowUpdate has none. */
  public update(
    data: WorkflowUpdate,
    workflowId: string | null,
    projectId: string | null
  ): Observable<object> {
    if (workflowId === null || projectId === null) {
      return EMPTY;
    }
    return this.http.put<Workflow>(
      `${API_BASE}/project/${projectId}/v/${this.#appState.versionId()}/workflow/${workflowId}`,
      data
    );
  }

  // ─────────────────────────────────────────────────────────────────────
  // Delete

  public delete(
    workflowId: string | null,
    projectId: string | null
  ): Observable<object> {
    if (workflowId === null && projectId === null) {
      return EMPTY;
    }
    return this.http.delete<Workflow>(
      `${API_BASE}/project/${projectId}/v/${this.#appState.versionId()}/workflow/${workflowId}`
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Set Active Workflow Id

  public setWorkflowId(id: number) {
    if (typeof id === 'number') {
      this.#appState.navigateToWorkflow(id);
      this.$setWorkflowIdSubject.next(`${id}`);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Get Workflow List

  public getWorkflowList(): Workflow[] | any {
    let workflowList = LocalstorageHelper.getWorkflowList();

    if (workflowList !== null) {
      workflowList = GeneralHelpers.jsonParse(workflowList);
      return workflowList;
    }

    return [];
  }

  // ─────────────────────────────────────────────────────────────────────
  // Get selected workflow

  public getSelectedWorkflow(): Workflow | any {
    const workflowId = this.#appState.workflowId().toString();
    const workflowList = this.getWorkflowList();
    let selectedWorkflow: Workflow | any = {};

    if (workflowId !== '' && workflowList.length > 0) {
      for (const element of workflowList) {
        if (workflowId == element.workflowId) {
          selectedWorkflow = element;
          break;
        }
      }
    }

    if (workflowId === '' && workflowList.length > 0) {
      selectedWorkflow = workflowList[0];
      this.setWorkflowId(selectedWorkflow.workflowId);
    }

    return selectedWorkflow;
  }

  // Legacy method names for backward compatibility
  public getNotebookList(): Workflow[] | any {
    return this.getWorkflowList();
  }

  public getSelectedNotebook(): Workflow | any {
    return this.getSelectedWorkflow();
  }

  public setNotebookId(id: number) {
    return this.setWorkflowId(id);
  }

  /**
   * Get all workflows for a project version with caching
   * @param projectId The ID of the project
   * @param versionId The ID of the version
   */
  public getAllWorkflows(projectId: number, versionId: number): Observable<Workflow[]> {
    const cacheKey = `${projectId}_${versionId}`;
    const cached = this.workflowsCache.get(cacheKey);

    // Return cached data if still valid
    if (cached && (Date.now() - cached.timestamp) < this.CACHE_DURATION) {
      if (cached.data) {
        console.log('📦 WorkflowService: Returning cached workflows');
        return of(cached.data);
      }
      // If there's a pending request, share it
      if (cached.request$) {
        console.log('⏳ WorkflowService: Reusing pending workflows request');
        return cached.request$;
      }
    }

    // Make a new request
    console.log('🌐 WorkflowService: Making new API request for workflows');
    const request$ = this.http.get<Workflow[]>(
      `${API_BASE}/project/${projectId}/v/${versionId}/workflow`
    ).pipe(
      catchError((error) => {
        if (error?.status === 404) {
          return of([]);
        }
        return throwError(() => error);
      }),
      tap(data => {
        // Cache the result
        this.workflowsCache.set(cacheKey, {
          data,
          timestamp: Date.now(),
          request$: null
        });
        console.log('✅ WorkflowService: Workflows cached');
      }),
      shareReplay(1)
    );

    // Store the pending request
    this.workflowsCache.set(cacheKey, {
      data: null,
      timestamp: Date.now(),
      request$
    });

    return request$;
  }

  /**
   * Get a full workflow with default files with caching
   * @param projectId The ID of the project
   * @param versionId The ID of the version
   * @param workflowId The ID of the workflow
   */
  public getFullWorkflow(projectId: number, versionId: number, workflowId: number): Observable<FullWorkflow> {
    const cacheKey = `${projectId}_${versionId}_${workflowId}`;
    const cached = this.fullWorkflowCache.get(cacheKey);

    // Return cached data if still valid
    if (cached && (Date.now() - cached.timestamp) < this.CACHE_DURATION) {
      if (cached.data) {
        console.log('📦 WorkflowService: Returning cached full workflow');
        return of(cached.data);
      }
      // If there's a pending request, share it
      if (cached.request$) {
        console.log('⏳ WorkflowService: Reusing pending full workflow request');
        return cached.request$;
      }
    }

    // Make a new request
    console.log('🌐 WorkflowService: Making new API request for full workflow');
    const request$ = this.http.get<FullWorkflow>(
      `${API_BASE}/project/${projectId}/v/${versionId}/workflow/${workflowId}`
    ).pipe(
      tap(data => {
        // Cache the result
        this.fullWorkflowCache.set(cacheKey, {
          data,
          timestamp: Date.now(),
          request$: null
        });
        console.log('✅ WorkflowService: Full workflow cached');
      }),
      shareReplay(1)
    );

    // Store the pending request
    this.fullWorkflowCache.set(cacheKey, {
      data: null,
      timestamp: Date.now(),
      request$
    });

    return request$;
  }

  /**
   * Create a workflow with version
   * @param projectId The ID of the project
   * @param versionId The ID of the version
   * @param workflowData The workflow data
   */
  public createWorkflowWithVersion(projectId: number, versionId: number, workflowData: any): Observable<Workflow> {
    return this.http.post<Workflow>(
      `${API_BASE}/project/${projectId}/v/${versionId}/workflow`,
      workflowData
    );
  }

  /**
   * Delete a workflow with version
   * @param projectId The ID of the project
   * @param versionId The ID of the version
   * @param workflowId The ID of the workflow
   */
  public deleteWorkflowWithVersion(
    projectId: number,
    versionId: number,
    workflowId: number,
    deleteHistory = false,
  ): Observable<void> {
    // A workflow whose steps have run is deleted only with delete_history, which
    // also deletes the jobs that ran them (EHQ-512).
    const options = deleteHistory ? { params: { delete_history: 'true' } } : {};
    return this.http.delete<void>(
      `${API_BASE}/project/${projectId}/v/${versionId}/workflow/${workflowId}`,
      options,
    );
  }

  /**
   * The job history deleting a workflow would remove: the jobs that ran its
   * steps plus every job of the routines connected to it, and how many are still
   * executing (EHQ-512).
   * GET /api/project/{projectId}/v/{versionId}/workflow/{workflowId}/deletion-impact
   */
  public getDeletionImpact(
    projectId: number,
    versionId: number,
    workflowId: number,
  ): Observable<WorkflowDeletionImpact> {
    return this.http.get<WorkflowDeletionImpact>(
      `${API_BASE}/project/${projectId}/v/${versionId}/workflow/${workflowId}/deletion-impact`,
    );
  }

  /**
   * Get the variable schema for a workflow.
   * GET /api/project/{projectId}/v/{versionId}/workflow/{workflowId}/variable-schema
   */
  public getVariableSchema(
    projectId: number,
    versionId: number,
    workflowId: number
  ): Observable<Record<string, unknown> | null> {
    return this.http.get<Record<string, unknown> | null>(
      `${API_BASE}/project/${projectId}/v/${versionId}/workflow/${workflowId}/variable-schema`
    );
  }

  /**
   * Set (or replace) the variable schema on a workflow.
   * PUT /api/project/{projectId}/v/{versionId}/workflow/{workflowId}/variable-schema
   */
  public setVariableSchema(
    projectId: number,
    versionId: number,
    workflowId: number,
    schema: Record<string, unknown>
  ): Observable<Workflow> {
    return this.http.put<Workflow>(
      `${API_BASE}/project/${projectId}/v/${versionId}/workflow/${workflowId}/variable-schema`,
      schema
    );
  }

  /**
   * Clear the variable schema from a workflow.
   * DELETE /api/project/{projectId}/v/{versionId}/workflow/{workflowId}/variable-schema
   */
  public clearVariableSchema(
    projectId: number,
    versionId: number,
    workflowId: number
  ): Observable<Workflow> {
    return this.http.delete<Workflow>(
      `${API_BASE}/project/${projectId}/v/${versionId}/workflow/${workflowId}/variable-schema`
    );
  }

  /**
   * Removes the cached workflow list for a given project/version so the next
   * call to getAllWorkflows() fetches a fresh list from the server.
   */
  public invalidateWorkflowsCache(projectId: number, versionId: number): void {
    const cacheKey = `${projectId}_${versionId}`;
    this.workflowsCache.delete(cacheKey);
  }
}
